import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Link, List, ListItem, Stack, Tooltip, Typography } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { Fragment, useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { Contactmoment } from '../api/typen.ts';
import { datumTijd, perDag, tijd } from '../helpers/formatteer.ts';
import { contactkanaalLabels, contactrichtingLabels } from '../helpers/labels.ts';
import { useZoekparameters } from '../hooks/useZoekparameters.ts';
import {
  type Chatpartner,
  chatparameter,
  chatpartnerVan,
  Kanaalicoon,
  leesChatparameter,
  useGesprekBewerken,
} from './gesprek.tsx';
import { GesprekChat } from './GesprekChat.tsx';
import { Laadstatus } from './Laadstatus.tsx';
import { Lijstpaginering } from './Paginering.tsx';
import {
  BedrijfVerwijzing,
  ContactpersoonVerwijzing,
  GebruikerNaam,
  KandidaatVerwijzing,
  VacatureVerwijzing,
} from './Verwijzingen.tsx';

/** Welke verwijzingen een gesprek toont. Op de pagina van een kandidaat hoeft de kandidaat er niet bij. */
interface Toon {
  kandidaat?: boolean;
  contactpersoon?: boolean;
  bedrijf?: boolean;
  vacature?: boolean;
  /** Wie het gesprek vastlegde; overbodig in een lijst met alleen je eigen gesprekken. */
  gebruiker?: boolean;
}

interface Props {
  query: UseQueryResult<Contactmoment[]>;
  toon?: Toon | undefined;
  leeg?: string | undefined;
  paginering?: { offset: number; limiet: number; onOffset: (offset: number) => void } | undefined;
  compact?: boolean | undefined;
}

/**
 * Een lijst gesprekken, nieuwste eerst en per dag gegroepeerd. Per gesprek staat bovenaan met wie het was, daaronder
 * het kanaal en de richting en dan de notitie, afgekapt na een paar regels. Een klik op een gesprek opent rechts de
 * chat met alle gesprekken met die persoon. Wie een gesprek vastlegde, of een beheerder, kan het wijzigen of
 * verwijderen. In de compacte vorm, zoals op het dashboard, staan de gesprekken zonder dagkoppen en zonder knoppen.
 * @param props De query met gesprekken, welke verwijzingen getoond worden en de paginering.
 * @returns De lijst.
 */
export function ContactmomentenLijst({
  query,
  toon,
  leeg = 'Nog geen gesprekken vastgelegd.',
  paginering,
  compact = false,
}: Props) {
  const bewerken = useGesprekBewerken();
  const { krijg, zet } = useZoekparameters();
  const chat = leesChatparameter(krijg('chat'));
  const openChat = (partner: Chatpartner) => zet({ chat: chatparameter(partner) }, true);
  const sluitChat = useCallback(() => zet({ chat: undefined }, true), [zet]);
  const zichtbaar: Required<Toon> = {
    kandidaat: true,
    contactpersoon: true,
    bedrijf: true,
    vacature: true,
    gebruiker: true,
    ...toon,
  };

  const regel = (moment: Contactmoment) => {
    const partner = chatpartnerVan(moment);
    const magBewerken = !compact && bewerken.magBewerken(moment);
    return (
      <Gesprek
        key={moment.id}
        moment={moment}
        toon={zichtbaar}
        compact={compact}
        geselecteerd={partner !== null && chat !== null && chatparameter(partner) === chatparameter(chat)}
        onOpen={partner ? () => openChat(partner) : undefined}
        onWijzig={magBewerken ? () => bewerken.wijzig(moment) : undefined}
        onVerwijder={magBewerken ? () => bewerken.verwijder(moment) : undefined}
      />
    );
  };

  return (
    <>
      <Laadstatus query={query} leeg={leeg}>
        {(momenten) => (
          <>
            {compact ? (
              <List disablePadding>{momenten.map(regel)}</List>
            ) : (
              perDag(momenten, (moment) => moment.tijdstip).map((dag, index) => (
                <Box key={dag.sleutel} component="section" aria-label={dag.kop} sx={{ mt: index === 0 ? 0 : 2 }}>
                  <Typography
                    variant="overline"
                    component="h3"
                    color="text.secondary"
                    sx={{ display: 'block', lineHeight: 2, borderBottom: 1, borderColor: 'divider' }}
                  >
                    {dag.kop}
                  </Typography>
                  <List disablePadding>{dag.items.map(regel)}</List>
                </Box>
              ))
            )}
            {paginering && <Lijstpaginering {...paginering} aantal={momenten.length} />}
          </>
        )}
      </Laadstatus>
      {bewerken.dialogen}
      <GesprekChat partner={chat} onSluit={sluitChat} />
    </>
  );
}

interface GesprekProps {
  moment: Contactmoment;
  toon: Required<Toon>;
  compact: boolean;
  geselecteerd: boolean;
  onOpen: (() => void) | undefined;
  onWijzig: (() => void) | undefined;
  onVerwijder: (() => void) | undefined;
}

function Gesprek({ moment, toon, compact, geselecteerd, onOpen, onWijzig, onVerwijder }: GesprekProps) {
  const kanaal = contactkanaalLabels[moment.kanaal];
  const richting = contactrichtingLabels[moment.richting].toLowerCase();

  const wie: ReactNode[] = [];
  if (toon.kandidaat && moment.kandidaatId) {
    wie.push(<KandidaatVerwijzing key="kandidaat" id={moment.kandidaatId} />);
  }
  if (toon.contactpersoon && moment.contactpersoonId) {
    wie.push(<ContactpersoonVerwijzing key="contactpersoon" id={moment.contactpersoonId} />);
  }
  if (toon.bedrijf && moment.bedrijfId) {
    wie.push(<BedrijfVerwijzing key="bedrijf" id={moment.bedrijfId} />);
  }
  const vacature = toon.vacature && moment.vacatureId ? moment.vacatureId : null;
  const heeftKop = wie.length > 0 || vacature !== null;

  const kenmerken: ReactNode[] = [
    ...(heeftKop ? [kanaal] : []),
    heeftKop ? richting : richting.charAt(0).toUpperCase() + richting.slice(1),
    ...(toon.gebruiker ? [<GebruikerNaam key="door" id={moment.gebruikerId} />] : []),
  ];

  return (
    <ListItem
      divider
      disableGutters
      alignItems="flex-start"
      onClick={(gebeurtenis) => {
        // Een klik op een naam of knop doet wat die naam of knop doet; elders in het gesprek opent de chat.
        if (onOpen && !(gebeurtenis.target as HTMLElement).closest('a, button')) {
          onOpen();
        }
      }}
      sx={{
        gap: 1.5,
        py: compact ? 1 : 1.5,
        px: 1,
        mx: -1,
        width: 'auto',
        borderRadius: 1,
        cursor: onOpen ? 'pointer' : 'default',
        bgcolor: geselecteerd ? 'action.selected' : undefined,
        '&:hover': onOpen ? { bgcolor: geselecteerd ? 'action.selected' : 'action.hover' } : {},
        '&:last-of-type': { borderBottom: 0 },
        '& .gesprek-acties': { opacity: { md: geselecteerd ? 1 : 0.35 }, transition: 'opacity 120ms' },
        '&:hover .gesprek-acties, &:focus-within .gesprek-acties': { opacity: 1 },
      }}
    >
      <Box sx={{ mt: 0.25 }}>
        <Kanaalicoon moment={moment} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography component="div" sx={{ fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>
            {heeftKop ? (
              <>
                {wie.map((deel, index) => (
                  <Fragment key={index}>
                    {index > 0 && <Scheiding />}
                    {deel}
                  </Fragment>
                ))}
                {vacature && (
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                    {wie.length > 0 ? ' over ' : 'Over '}
                    <VacatureVerwijzing id={vacature} />
                  </Box>
                )}
              </>
            ) : (
              kanaal
            )}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            title={datumTijd(moment.tijdstip)}
            sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
          >
            {compact ? datumTijd(moment.tijdstip) : tijd(moment.tijdstip)}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {kenmerken.map((kenmerk, index) => (
            <Fragment key={index}>
              {index > 0 && <Scheiding />}
              {kenmerk}
            </Fragment>
          ))}
        </Typography>
        {moment.notitie && <Notitie tekst={moment.notitie} regels={compact ? 2 : 3} />}
      </Box>
      {(onOpen || onWijzig || onVerwijder) && (
        <Stack direction="row" className="gesprek-acties" sx={{ mt: -0.5, mr: -0.5 }}>
          {onOpen && (
            <Tooltip title="Open als chat">
              <IconButton size="small" aria-label="Open als chat" onClick={onOpen}>
                <ChatBubbleOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onWijzig && (
            <Tooltip title="Wijzigen">
              <IconButton size="small" aria-label="Gesprek wijzigen" onClick={onWijzig}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onVerwijder && (
            <Tooltip title="Verwijderen">
              <IconButton size="small" aria-label="Gesprek verwijderen" onClick={onVerwijder}>
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </ListItem>
  );
}

function Scheiding() {
  return (
    <Box component="span" aria-hidden sx={{ color: 'text.disabled', mx: 0.75, fontWeight: 400 }}>
      ·
    </Box>
  );
}

/** Een notitie die na een paar regels afkapt, met een link om hem in de lijst helemaal te lezen. */
function Notitie({ tekst, regels }: { tekst: string; regels: number }) {
  const [open, setOpen] = useState(false);
  const [teLang, setTeLang] = useState(false);
  const element = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open && element.current) {
      setTeLang(element.current.scrollHeight > element.current.clientHeight + 1);
    }
  }, [tekst, open, regels]);

  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography
        ref={element}
        component="span"
        variant="body2"
        sx={{
          display: open ? 'block' : '-webkit-box',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          ...(open ? {} : { WebkitLineClamp: regels, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        }}
      >
        {tekst}
      </Typography>
      {(teLang || open) && (
        <Link component="button" type="button" variant="body2" onClick={() => setOpen(!open)} sx={{ mt: 0.25 }}>
          {open ? 'Minder tonen' : 'Meer tonen'}
        </Link>
      )}
    </Box>
  );
}
